import { onUnmounted, getCurrentInstance } from 'vue';
import { useIframeOrigins } from '@/composables/useIframeOrigins';
import {
  IFRAME_ACTIONS,
  IFRAME_MESSAGE_TYPES,
} from '@/composables/iframeBridgeProtocol';
import { isOriginAllowedByIframePatterns } from '@/utils/iframeTrustedOrigins';

export type MessageDataObject<T = any> = {
  type: string;
  action: string;
  data?: T;
};

type MessageHandler = (
  eventData: MessageDataObject,
  rawEvent?: MessageEvent<MessageDataObject>,
) => void;

export const IFRAME_RESIZE_TYPE = IFRAME_MESSAGE_TYPES.IFRAME_RESIZE;
export const FULLSCREEN_OPEN_ACTION = IFRAME_ACTIONS.FULLSCREEN_OPEN;
export const FULLSCREEN_CLOSE_ACTION = IFRAME_ACTIONS.FULLSCREEN_CLOSE;

export const useIframeConnection = () => {
  const CLOSE_MODAL_ACTION = IFRAME_ACTIONS.CLOSE_MODAL;
  const { childOrigin, allowedOriginPatterns } = useIframeOrigins('crm');

  const sendToIframe = (iframe: HTMLIFrameElement, data: MessageDataObject) => {
    iframe.contentWindow?.postMessage(data, childOrigin.value);
  };

  /**
   * Picks the iframe a message should go to. A message reaches the preloaded
   * iframe only when its `type` equals the modal currently shown there
   * (`data-active-modal-type`); everything else routes to the legacy iframe.
   * Reading both the active type and the message type locally keeps the
   * transport free of feature-state imports (no cycle) and makes routing
   * correct-by-construction — independent of close/flush timing.
   */
  const resolveCrmIframeFor = (data: MessageDataObject): HTMLIFrameElement | null => {
    const preloaded = document.querySelector<HTMLIFrameElement>('iframe[data-preloaded="true"]');
    const activeModalType = preloaded?.dataset.activeModalType;
    if (activeModalType && activeModalType === data.type) {
      return preloaded;
    }
    // Must name the legacy CRM modal iframe explicitly: LIframe.vue renders a
    // dozen other unmarked iframes (the AI page, the calculator, the V3 log
    // panels), and picking "the first iframe that is not preloaded" routed CRM
    // messages by DOM order — dropped on a foreign origin, or worse, delivered
    // to a different V3 iframe that happens to share the origin.
    return document.querySelector<HTMLIFrameElement>('iframe[data-crm-modal="true"]');
  };

  const send = (data: MessageDataObject) => {
    const iframe = resolveCrmIframeFor(data);
    if (iframe) {
      iframe.contentWindow?.postMessage(data, childOrigin.value);
    }
  };

  const listen = (handler: MessageHandler) => {
    const handlerWrapper = (event: MessageEvent<MessageDataObject>) => {
      const selfOrigin = window.location.origin;
      if (
        !isOriginAllowedByIframePatterns(event.origin, allowedOriginPatterns.value)
        && event.origin !== selfOrigin
      ) {
        return;
      }
      if (!event.data || typeof event.data !== 'object') {
        return;
      }
      return handler(event.data, event);
    };

    window.addEventListener('message', handlerWrapper);
    const stop = () => window.removeEventListener('message', handlerWrapper);
    if (getCurrentInstance()) {
      onUnmounted(stop);
    }
    return stop;
  };

  return {
    CLOSE_MODAL_ACTION,
    send,
    sendToIframe,
    listen,
  };
};
